import React from 'react';
import { useTranslation } from 'react-i18next';
import { Compass, ExternalLink, Globe2, LifeBuoy, LogOut, Monitor, Moon, Settings, Sun, type LucideIcon } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
} from 'components/ui/dropdown-menu';
import { Avatar } from 'components/ui/avatar';
import * as AuthModule from 'modules/auth';
import { useProductTour } from 'modules/tour';
import { useSettingsDialog } from 'components/settings/useSettingsDialog';
import { useLanguageStore, type Language } from 'store/language.store';
import { useThemeStore, type Theme } from 'store/theme.store';

const LANGUAGES: { code: Language; label: string }[] = [
  { code: 'uz', label: "O'zbek" },
  { code: 'en', label: 'English' },
  { code: 'ru', label: 'Русский' },
];

const THEMES: { code: Theme; icon: LucideIcon; labelKey: string }[] = [
  { code: 'light', icon: Sun, labelKey: 'usermenu_themelight' },
  { code: 'dark', icon: Moon, labelKey: 'usermenu_themedark' },
  { code: 'system', icon: Monitor, labelKey: 'usermenu_themesystem' },
];

const TELEGRAM_SUPPORT = 'https://t.me/yoqub_sobirjonov';

/** The single account entry point, since the user block moved out of the sidebar footer. */
export function HeaderProfile() {
  const { t } = useTranslation();
  const { open: openSettings } = useSettingsDialog();
  const { language, setLanguage } = useLanguageStore();
  const { theme, setTheme } = useThemeStore();
  const { resetTour } = useProductTour();
  const {
    state: { user },
    methods,
  } = AuthModule.Context.useAuthContext();

  if (!user) return null;

  // Phone-only accounts have no email; fall back to a job title, then nothing.
  const subtitle = user.email || user.jobTitle || undefined;
  const activeTheme = THEMES.find((option) => option.code === theme) ?? THEMES[0];
  const ActiveThemeIcon = activeTheme.icon;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        {/* Sized as a flex box, not a plain inline button: an inline-level
            avatar reserves line-box descender space, so the ring rendered as a
            tall oval around the circle instead of hugging it. */}
        <button
          type="button"
          data-tour="header-profile"
          id="header-profile-btn"
          className="flex h-8 w-8 shrink-0 items-center justify-center overflow-hidden rounded-full ring-1 ring-border transition-opacity hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:ring-offset-background"
          aria-label={t('usermenu_account', 'Account')}
        >
          <Avatar src={user.avatarUrl} name={user.name} size="sm" isCurrentUser className="h-full w-full rounded-full" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" sideOffset={8} className="w-60 p-1.5">
        <div className="flex items-center gap-2.5 px-2 py-2">
          <Avatar src={user.avatarUrl} name={user.name} size="sm" isCurrentUser className="shrink-0 rounded-full" />
          <div className="min-w-0">
            <p className="truncate text-sm font-semibold text-foreground">{user.name}</p>
            {subtitle && <p className="truncate text-[11px] text-muted-foreground">{subtitle}</p>}
          </div>
        </div>
        <DropdownMenuSeparator />
        <DropdownMenuItem className="gap-2.5" onClick={() => openSettings('profile')}>
          <Settings className="h-4 w-4 shrink-0 text-muted-foreground" />
          {t('settings.title')}
        </DropdownMenuItem>
        <DropdownMenuItem className="gap-2.5" onClick={() => resetTour()}>
          <Compass className="h-4 w-4 shrink-0 text-muted-foreground" />
          {t('tour.start_tour', "Tizim bo'yicha sayohat")}
        </DropdownMenuItem>
        <DropdownMenuItem asChild className="gap-2.5 cursor-pointer">
          <a
            href={TELEGRAM_SUPPORT}
            target="_blank"
            rel="noopener noreferrer"
          >
            <LifeBuoy className="h-4 w-4 shrink-0 text-muted-foreground" />
            <span>{t('usermenu_support', "Qo'llab-quvvatlash")}</span>
            <ExternalLink className="ml-auto h-3.5 w-3.5 text-muted-foreground/60" aria-hidden="true" />
          </a>
        </DropdownMenuItem>
        <DropdownMenuSub>
          <DropdownMenuSubTrigger
            className="gap-2.5 [&>svg:last-child]:!ml-1"
            data-account-preference="language"
          >
            <Globe2 className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
            <span>{t('settings.display_language', 'Language')}</span>
            <span className="ml-auto text-xs text-muted-foreground">
              {LANGUAGES.find((option) => option.code === language)?.label}
            </span>
          </DropdownMenuSubTrigger>
          <DropdownMenuSubContent className="min-w-36">
            <DropdownMenuRadioGroup value={language} onValueChange={(value) => setLanguage(value as Language)}>
              {LANGUAGES.map((option) => (
                <DropdownMenuRadioItem key={option.code} value={option.code}>
                  {option.label}
                </DropdownMenuRadioItem>
              ))}
            </DropdownMenuRadioGroup>
          </DropdownMenuSubContent>
        </DropdownMenuSub>
        <DropdownMenuSub>
          <DropdownMenuSubTrigger
            className="gap-2.5 [&>svg:last-child]:!ml-1"
            data-account-preference="theme"
          >
            <ActiveThemeIcon className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
            <span>{t('usermenu_theme', 'Appearance')}</span>
            <span className="ml-auto text-xs text-muted-foreground">
              {t(activeTheme.labelKey)}
            </span>
          </DropdownMenuSubTrigger>
          <DropdownMenuSubContent className="min-w-36">
            <DropdownMenuRadioGroup value={theme} onValueChange={(value) => setTheme(value as Theme)}>
              {THEMES.map((option) => {
                const Icon = option.icon;
                return (
                  <DropdownMenuRadioItem key={option.code} value={option.code} className="gap-2">
                    <Icon className="h-3.5 w-3.5 text-muted-foreground" aria-hidden />
                    {t(option.labelKey)}
                  </DropdownMenuRadioItem>
                );
              })}
            </DropdownMenuRadioGroup>
          </DropdownMenuSubContent>
        </DropdownMenuSub>
        <DropdownMenuSeparator />
        <DropdownMenuItem className="gap-2.5" onClick={() => methods.logout()}>
          <LogOut className="h-4 w-4 shrink-0 text-muted-foreground" />
          {t('usermenu_signout')}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export default HeaderProfile;
